/**
 * ExamApplication Types for MEV-Med Frontend
 * Type definitions for exam application management across organizational levels
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de aplicações de exames em 4 níveis organizacionais
 */

import { ROTULO_DO_NIVEL } from './nivelDeAplicacao';
import { ApplicationLevel } from './riskApplication';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Application levels in the organizational hierarchy — a lista única de níveis (#541)
 */
export { ApplicationLevel };

/**
 * Expiration status for exam applications
 */
export enum ExpirationStatus {
  OK = 'OK',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
}

// ============================================================================
// APPLICATION DATA INTERFACES
// ============================================================================

/**
 * Data for creating/updating an exam application
 * Used in apply and update operations
 */
export interface ApplicationData {
  periodicityMonths?: number | null;
  isRequired?: boolean;
  observations?: string | null;
}

/**
 * Request payload for applying an exam
 */
export interface ApplyExamRequest {
  examId: number;
  periodicityMonths?: number | null;
  isRequired?: boolean;
  observations?: string | null;
}

/**
 * Request payload for updating an application
 */
export interface UpdateApplicationRequest {
  periodicityMonths?: number | null;
  isRequired?: boolean;
  observations?: string | null;
}

// ============================================================================
// EXAM WITH APPLICATION (Applied Exams)
// ============================================================================

/**
 * Exam with application details
 * Represents an exam that has been applied to a target (company, department, occupation, or employee)
 */
export interface ExamWithApplication {
  id: number;
  examId: number;
  examName: string;
  esocialCode?: string | null;
  tussCode?: string | null;
  periodicityMonths?: number | null;
  isRequired: boolean;
  observations?: string | null;
  applicationLevel: ApplicationLevel;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// AVAILABLE EXAM (Not Yet Applied)
// ============================================================================

/**
 * Available exam for application
 * Represents an exam that can be applied to a target
 */
export interface AvailableExam {
  id: number;
  examName: string;
  esocialCode?: string | null;
  tussCode?: string | null;
  defaultFrequency?: string | null;
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

/**
 * Response for get exams endpoint (applied + available)
 */
export interface ExamsForTargetResponse {
  applied: ExamWithApplication[];
  available: AvailableExam[];
}

/**
 * Generic API success response
 */
export interface ExamApplicationSuccessResponse {
  message: string;
  code: string;
}

/**
 * API error response
 */
export interface ExamApplicationErrorResponse {
  error: string;
  code: string;
}

// ============================================================================
// TARGET INFORMATION
// ============================================================================

/**
 * Target entity for application context
 */
export interface ApplicationTarget {
  id: number;
  name: string;
  level: ApplicationLevel;
  parentId?: number;
  parentName?: string;
}

/**
 * Hierarchical target with full path
 */
export interface ApplicationTargetWithPath extends ApplicationTarget {
  companyId: number;
  companyName: string;
  departmentId?: number;
  departmentName?: string;
  occupationId?: number;
  occupationName?: string;
}

// ============================================================================
// UI COMPONENT PROPS
// ============================================================================

/**
 * Props for DualTransferList component
 */
export interface ExamTransferListProps {
  applied: ExamWithApplication[];
  available: AvailableExam[];
  onApply: (examIds: number[], data?: ApplicationData) => Promise<void>;
  onRemove: (examIds: number[]) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Props for ExamApplicationModal (for setting application details)
 */
export interface ExamApplicationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: ApplicationData) => Promise<void>;
  exam: AvailableExam | ExamWithApplication | null;
  mode: 'apply' | 'edit';
  loading?: boolean;
}

// ============================================================================
// FILTER INTERFACES
// ============================================================================

/**
 * Filters for exam applications list
 */
export interface ExamApplicationFilters {
  targetLevel?: ApplicationLevel;
  targetId?: number;
  expirationStatus?: ExpirationStatus;
  isRequired?: boolean;
  search?: string;
}

// ============================================================================
// LABELS AND CONSTANTS
// ============================================================================

/**
 * Labels for application levels (Portuguese)
 */
export const ApplicationLevelLabels: Record<ApplicationLevel, string> = ROTULO_DO_NIVEL;

/**
 * Labels for expiration status (Portuguese)
 */
export const ExpirationStatusLabels: Record<ExpirationStatus, string> = {
  [ExpirationStatus.OK]: 'Em dia',
  [ExpirationStatus.EXPIRING_SOON]: 'Vencendo',
  [ExpirationStatus.EXPIRED]: 'Vencido',
};

/**
 * Colors for expiration status badges
 */
export const ExpirationStatusColors: Record<ExpirationStatus, 'success' | 'warning' | 'error'> = {
  [ExpirationStatus.OK]: 'success',
  [ExpirationStatus.EXPIRING_SOON]: 'warning',
  [ExpirationStatus.EXPIRED]: 'error',
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Exame aplicado ou disponível: só o aplicado traz `applicationLevel`. O teste não olha as marcas
 * de vencimento, que a aplicação deixa de ter (#567) — com elas, editar abriria como aplicação nova
 * e recarregaria a periodicidade do cadastro por cima da escolhida.
 */
export function isExamWithApplication(
  exam: ExamWithApplication | AvailableExam
): exam is ExamWithApplication {
  return 'applicationLevel' in exam;
}

/**
 * Format days until expiration for display
 */
export function formatDaysUntilExpiration(days: number | null | undefined): string {
  if (days === null || days === undefined) {
    return 'N/A';
  }
  if (days < 0) {
    const overdue = Math.abs(days);
    return `${overdue} dia${overdue !== 1 ? 's' : ''} vencido`;
  }
  if (days === 0) {
    return 'Vence hoje';
  }
  return `${days} dia${days !== 1 ? 's' : ''}`;
}
